import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./AuthorProfile.css";
import TextAdsPage from "./TextAdsPage";
import ImageAdsPage from "./ImageAdsPage";
import StatusAvatar from "./StatusAvatar";
import useStatusIndex from "../hooks/useStatusIndex";
import useSmartLinks from "../hooks/useSmartLinks";
import Comments from "./Comments";
import LikeButton from "./LikeButton";
// Step 7 — the single read-only question behind the "Watch Pro" button.
import { checkProCreator } from "../services/proSystemApi";
import ListLoader, { useProgressiveList } from "./ProgressiveList";

const BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

const AV_COLORS = ["#8b5cf6","#06b6d4","#ec4899","#f59e0b","#10b981","#3b82f6","#ef4444"];
function makeAvatar(name = "?") {
  const initials = name.trim().split(/\s+/).map(w => w[0] || "").join("").toUpperCase().slice(0,2) || "?";
  const color    = AV_COLORS[name.charCodeAt(0) % AV_COLORS.length];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
    <circle cx="50" cy="50" r="50" fill="${color}"/>
    <text x="50" y="50" dominant-baseline="central" text-anchor="middle"
      fill="white" font-size="38" font-family="sans-serif" font-weight="700">${initials}</text>
  </svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function Avatar({ src, name = "?", size = 36, className = "" }) {
  const [err, setErr] = useState(false);
  return (
    <img
      src={!src || err ? makeAvatar(name) : src}
      alt={name}
      onError={() => setErr(true)}
      className={className}
      style={{
        width: size, height: size,
        borderRadius: "50%",
        objectFit: "cover",
        objectPosition: "center top",
        display: "block",
        flexShrink: 0,
        border: "2px solid #080b14",
        background: "#1a1040",
      }}
    />
  );
}

function AuthorBlogs() {
  const smartLinks  = useSmartLinks("authorprofile");
  const { author }  = useParams();
  const navigate    = useNavigate();
  const canvasRef   = useRef(null);
  const videoRefs   = useRef({});

  const { getStatusFor } = useStatusIndex();
  const authorStatus     = getStatusFor(author);

  const [statusToast, setStatusToast] = useState(false);

  const openAuthorStatus = () => {
    if (!authorStatus?.userId) {
      setStatusToast(true);
      setTimeout(() => setStatusToast(false), 2000);
      return;
    }
    navigate(`/view-status?user=${authorStatus.userId}`);
  };

  const [blogs, setBlogs]                   = useState([]);
  const [loading, setLoading]               = useState(true);
  const [expandedBlogs, setExpandedBlogs]   = useState({});
  const [totalBlogs, setTotalBlogs]         = useState(0);
  const [authorProfile, setAuthorProfile]   = useState("");
  const [authorBio, setAuthorBio]           = useState("");
  const [profileLoading, setProfileLoading] = useState(true);
  const [followStatus, setFollowStatus]     = useState(false);
  const [followLoading, setFollowLoading]   = useState(false);
  const [followAnim, setFollowAnim]         = useState(false);
  const [blogsError, setBlogsError]         = useState("");
  const [openCommentId, setOpenCommentId]   = useState(null);
  const [shareToast, setShareToast]         = useState(null);
  const [modalImg, setModalImg]             = useState(null);
  const [myProfilePic, setMyProfilePic]     = useState("");
  const [followers, setFollowers]           = useState([]);
  const [following, setFollowing]           = useState([]);
  const [savedBlogs, setSavedBlogs]         = useState([]);
  const [commentCounts, setCommentCounts]   = useState({});
  const [shareCounts, setShareCounts]       = useState({});
  const [saveCounts, setSaveCounts]         = useState({});

  // ── BLOCK SYSTEM STATE ──
  const [authorId, setAuthorId]             = useState("");
  // Step 7 — whether this author has a TheBlogsSphere Pro page to watch.
  const [hasProPage, setHasProPage]         = useState(false);
  const [blockInfo, setBlockInfo]           = useState({ iBlocked: false, blockedMe: false, blocked: false });

  // Batch size for progressive loading (was the per-page size). Opening a
  // different author starts again from the first batch.
  const blogsPerPage = 10;
  const blogList     = useProgressiveList(blogs, blogsPerPage, author);

  const token    = typeof window !== "undefined" ? localStorage.getItem("token")    : null;
  const userId   = typeof window !== "undefined" ? localStorage.getItem("userId")   : null;
  const username = typeof window !== "undefined" ? localStorage.getItem("username") : "Anonymous";

  useEffect(() => {
    if (!username) return;
    axios
      .get(`${BASE_URL}/api/profile/username/${encodeURIComponent(username)}`)
      .then(res => { if (res.data?.profilePic) setMyProfilePic(res.data.profilePic); })
      .catch(() => {});
  }, [username]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const particles = Array.from({ length: 55 }, () => ({
      x:  Math.random() * canvas.width,
      y:  Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.28,
      vy: (Math.random() - 0.5) * 0.28,
      r:  Math.random() * 1.4 + 0.4,
    }));
    let animId;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx   = particles[i].x - particles[j].x;
          const dy   = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 110) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(139,92,246,${(1 - dist / 110) * 0.5})`;
            ctx.lineWidth = 0.4;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
        ctx.beginPath();
        ctx.arc(particles[i].x, particles[i].y, particles[i].r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(167,139,250,0.55)";
        ctx.fill();
        particles[i].x += particles[i].vx;
        particles[i].y += particles[i].vy;
        if (particles[i].x < 0 || particles[i].x > canvas.width)  particles[i].vx *= -1;
        if (particles[i].y < 0 || particles[i].y > canvas.height) particles[i].vy *= -1;
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);

  useEffect(() => {
    if (!author) return;
    fetchAuthorProfile();
    fetchAuthorBlogs();
    if (token) {
      checkFollowStatus();
      fetchFollowData();
      fetchSavedBlogs();
    }
  }, [author]);

  const checkFollowStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/users/${author}/follow-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFollowStatus(Boolean(res.data.isFollowing));
    } catch { setFollowStatus(false); }
  }, [author, token]);

  const fetchFollowData = useCallback(async () => {
    if (!token) return;
    try {
      const [f1, f2] = await Promise.all([
        axios.get(`${BASE_URL}/api/users/${author}/followers`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${BASE_URL}/api/users/${author}/following`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setFollowers(f1.data.followers || []);
      setFollowing(f2.data.following || []);
    } catch (err) { console.error("Failed to fetch follow data", err); }
  }, [author, token]);

  const fetchSavedBlogs = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${BASE_URL}/api/blogs/saved`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedBlogs((res.data.savedBlogs || []).map(b => (typeof b === "object" ? b._id : b)));
    } catch (err) { console.error("Failed to fetch saved blogs", err); }
  }, [token]);

  const handleSaveToggle = async (blogId) => {
    if (!token) { alert("Please login first"); return; }
    try {
      const res = await axios.post(`${BASE_URL}/api/blogs/${blogId}/save`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (typeof res.data?.saveCount === "number") {
        setSaveCounts(prev => ({ ...prev, [blogId]: res.data.saveCount }));
      }
      fetchSavedBlogs();
    } catch (err) { console.error("Save failed", err); }
  };

  const isSaved = (id) => savedBlogs.includes(id);

  const toggleFollow = async () => {
    if (!token) { alert("Please login first"); return; }
    if (followLoading) return;
    const prev = followStatus;
    setFollowStatus(!prev);
    setFollowLoading(true);
    setFollowAnim(true);
    setTimeout(() => setFollowAnim(false), 600);
    try {
      await axios.post(`${BASE_URL}/api/users/${author}/follow`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchFollowData();
    } catch { setFollowStatus(prev); }
    finally { setFollowLoading(false); }
  };

  const fetchAuthorProfile = async () => {
    try {
      setProfileLoading(true);
      const res = await axios.get(`${BASE_URL}/api/profile/username/${author}`);
      setAuthorProfile(res.data.profilePic || "");
      setAuthorBio(res.data.bio || "");
      setAuthorId(res.data._id || "");
    } catch {
      setAuthorProfile(""); setAuthorBio(""); setAuthorId("");
    } finally { setProfileLoading(false); }
  };

  const fetchBlockStatus = useCallback(async (targetId) => {
    if (!token || !targetId) return;
    try {
      const res = await axios.get(`${BASE_URL}/api/block/status/${targetId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlockInfo(res.data || { iBlocked: false, blockedMe: false, blocked: false });
    } catch { /* ignore — treat as not blocked */ }
  }, [token]);

  useEffect(() => {
    if (authorId && token) fetchBlockStatus(authorId);
  }, [authorId, token, fetchBlockStatus]);

  // ── WATCH PRO  (Step 7) ──
  // Additive and self-contained: one cheap question to the Pro system, asked
  // once the author's id is known. The button below renders ONLY when the
  // answer is yes, so a free author with no Pro page never grows a dead
  // button and nothing else on this page changes. A failed check simply means
  // no button — the free profile is never blocked on the Pro system.
  useEffect(() => {
    if (!authorId) {
      setHasProPage(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await checkProCreator(authorId);
        if (!cancelled) setHasProPage(Boolean(res?.isProCreator));
      } catch {
        if (!cancelled) setHasProPage(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authorId]);

  const fetchAuthorBlogs = async () => {
    try {
      setLoading(true);
      setBlogsError("");
      const res  = await axios.get(`${BASE_URL}/api/blogs/profile/${author}`);
      const data = (res.data.blogs || []).map(b => ({
        ...b,
        likes:     b.likes || [],
        likeCount: b.likes?.length || 0,
      }));
      setBlogs(data);
      setTotalBlogs(data.length);

      const comments = {}, shares = {}, saves = {};
      data.forEach(b => {
        comments[b._id] = b.commentCount ?? 0;
        shares[b._id]   = b.shareCount   ?? 0;
        saves[b._id]    = b.saveCount    ?? 0;
      });
      setCommentCounts(comments);
      setShareCounts(shares);
      setSaveCounts(saves);
    } catch (err) {
      console.error("Failed to fetch blogs", err);
      setBlogsError("Could not load this author's blogs. Please try again.");
    }
    finally { setLoading(false); }
  };

  const toggleComments = (blogId) => {
    setOpenCommentId(prev => (prev === blogId ? null : blogId));
  };

  const recordShare = async (blogId) => {
    if (!token) return;
    setShareCounts(prev => ({ ...prev, [blogId]: (prev[blogId] ?? 0) + 1 }));
    try {
      const res = await axios.post(`${BASE_URL}/api/blogs/${blogId}/share`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (typeof res.data?.shareCount === "number") {
        setShareCounts(prev => ({ ...prev, [blogId]: res.data.shareCount }));
      }
    } catch (err) {
      console.error("Share count failed", err);
      setShareCounts(prev => ({ ...prev, [blogId]: Math.max((prev[blogId] ?? 1) - 1, 0) }));
    }
  };

  const handleShare = async (blog) => {
    const shareUrl = `${window.location.origin}/blog/${blog._id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: blog.title, text: blog.title, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShareToast(blog._id);
        setTimeout(() => setShareToast(null), 2000);
      }
    } catch (err) {
      console.error("Share failed", err);
      return;
    }
    recordShare(blog._id);
  };

  const handleVideoPlay = (id) => {
    Object.keys(videoRefs.current).forEach(k => {
      if (k !== id && videoRefs.current[k] && !videoRefs.current[k].paused)
        videoRefs.current[k].pause();
    });
  };

  const toggleReadMore = (id) =>
    setExpandedBlogs(prev => ({ ...prev, [id]: !prev[id] }));

  const currentBlogs = blogList.visibleItems;

  const possessive = author
    ? author.trim().endsWith("s") || author.trim().endsWith("S")
      ? `${author.trim()}'`
      : `${author.trim()}'s`
    : "";

  if (loading) return (
    <div className="ap-wrapper">
      <canvas ref={canvasRef} className="ap-canvas" />
      <Navbar />
      <div className="ap-loading">
        <div className="ap-loading-spinner" />
        <p>Loading profile…</p>
      </div>
    </div>
  );

  if (blockInfo.blocked) return (
    <div className="ap-wrapper">
      <canvas ref={canvasRef} className="ap-canvas" />
      <Navbar />
      <div className="ap-loading">
        <div className="ap-empty-icon" style={{ fontSize: 48 }}>🚫</div>
        <p style={{ marginTop: 12 }}>This user is not available.</p>
      </div>
    </div>
  );

  return (
    <div className="ap-wrapper">
      <canvas ref={canvasRef} className="ap-canvas" />
      <Navbar />

      <div className="ap-page">

        {/* HEADER CARD */}
        <div className="ap-header-card">
          <div className="ap-orb ap-orb1" />
          <div className="ap-orb ap-orb2" />
          <div className="ap-scanner" />

          <div className="ap-avatar-wrap">
            <div className="ap-avatar-ring">
              {profileLoading ? (
                <div className="ap-avatar-skeleton" />
              ) : (
                <StatusAvatar
                  src={authorProfile}
                  alt={author}
                  size={96}
                  hasStatus={Boolean(authorStatus)}
                  hasUnviewed={Boolean(authorStatus?.hasUnviewed)}
                  onClick={openAuthorStatus}
                  title={`View ${author}'s status`}
                />
              )}
            </div>
          </div>

          <div className="ap-badge"><span className="ap-badge-dot" />Blogger</div>

          <div className="ap-name-block">
            <h1 className="ap-display-title">{possessive} Profile</h1>
            <div className="ap-handle-row">
              <span className="ap-handle-at">@</span>
              <span className="ap-handle-name">{author.toLowerCase().replace(/\s/g, "_")}</span>
            </div>
            {authorBio && <p className="ap-bio-text">{authorBio}</p>}
          </div>

          <div className="profile-stats-row">
            <div className="stat-item">
              <span className="stat-number">{totalBlogs}</span>
              <span className="stat-label">Blogs</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-number">{followers.length}</span>
              <span className="stat-label">Followers</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-number">{following.length}</span>
              <span className="stat-label">Following</span>
            </div>
          </div>

          <button
            className={`ap-follow-btn ${followStatus ? "ap-following" : ""} ${followAnim ? "ap-follow-pop" : ""}`}
            onClick={toggleFollow}
            disabled={followLoading}
          >
            {followLoading ? <span className="ap-btn-spinner" /> : followStatus ? "✓ Following" : "+ Follow"}
          </button>

          {/* ── WATCH PRO  (Step 7) ──
              Opens this creator's Pro page, where the first two Pro blogs are
              free and the rest unlock for ₹19. Rendered only when the author
              actually has a Pro page. */}
          {hasProPage && (
            <button
              className="ap-watch-pro-btn"
              type="button"
              onClick={() => navigate(`/pro-creator/${authorId}`)}
            >
              💎 Watch Pro
            </button>
          )}

        </div>

        {/* BLOGS HEADER */}
        <div className="blogs-section-header">
          <span className="blogs-section-line" />
          <h2 className="blogs-section-title">✦ Blogs</h2>
          <span className="blogs-section-line" />
        </div>

        {blogsError ? (
          <div className="plist-state" role="alert">
            <p>{blogsError}</p>
            <button type="button" className="plist-retry-btn" onClick={fetchAuthorBlogs}>
              Try again
            </button>
          </div>
        ) : currentBlogs.length === 0 ? (
          <div className="ap-empty">
            <div className="ap-empty-icon">📝</div>
            <p>No blogs published yet.</p>
          </div>
        ) : (
          currentBlogs.map((blog, i) => {
            const isExpanded    = expandedBlogs[blog._id];
            const isLiked       = blog.likes?.some(l =>
              typeof l === "object" ? l._id === userId : l === userId
            );
            const isCommentOpen = openCommentId === blog._id;
            const rawContent    = (blog.content || "").trim();
            const needsTruncate = rawContent.length > 200;

            // same guard pattern as ViewBlogs.js
            const commentsHidden = Boolean(blog.hideComments);
            const shareOff       = blog.allowShare === false;
            const saveOff        = blog.allowSave  === false;

            return (
              <div
                key={blog._id}
                className="ap-blog-card"
                style={{ animationDelay: `${i * 0.06}s` }}
                {...smartLinks.cardProps(i + 1)}
              >
                {/* AUTHOR ROW */}
                <div className="ap-card-author-row">
                  <StatusAvatar
                    src={authorProfile}
                    alt={author}
                    size={44}
                    hasStatus={Boolean(authorStatus)}
                    hasUnviewed={Boolean(authorStatus?.hasUnviewed)}
                    onClick={openAuthorStatus}
                    title={`View ${author}'s status`}
                  />
                  <div className="ap-card-author-info">
                    <span className="ap-card-author-name">{author}</span>
                    <span className="ap-card-date">
                      {new Date(blog.createdAt).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric",
                      })}
                    </span>
                  </div>
                </div>

                {/* TITLE */}
                {blog.title && <h3 className="ap-blog-title">{blog.title}</h3>}

                {/* CONTENT */}
                {rawContent.length > 0 && (
                  <p className="ap-blog-content">
                    {isExpanded || !needsTruncate ? rawContent : rawContent.slice(0, 200)}
                    {needsTruncate && (
                      <span
                        className="ap-read-more"
                        onClick={() => toggleReadMore(blog._id)}
                      >
                        {isExpanded ? " Read Less" : "… Read More"}
                      </span>
                    )}
                  </p>
                )}

                {/* IMAGE */}
                {blog.image && (
                  <div className="ap-media-wrap" onClick={() => setModalImg(blog.image)}>
                    <img src={blog.image} alt="blog visual" className="ap-blog-img" />
                    <div className="ap-media-hint">🔍 Tap to expand</div>
                  </div>
                )}

                {/* VIDEO */}
                {blog.video && (
                  <div className="ap-media-wrap ap-media-video-wrap">
                    <video
                      src={blog.video}
                      controls
                      className="ap-blog-video"
                      ref={el => (videoRefs.current[blog._id] = el)}
                      onPlay={() => handleVideoPlay(blog._id)}
                    />
                  </div>
                )}

                {/* ACTIONS — same layout + same guard pattern as ViewBlogs page */}
                <div className="blog-actions">
                  <div className="actions-left">
                    {/* ── LIKE — self-contained LikeButton ── */}
                    <LikeButton
                      blogId={blog._id}
                      initialLikes={blog.likeCount || 0}
                      initialLiked={isLiked}
                      token={token}
                    />

                    {/* ── COMMENT TOGGLE ── */}
                    {!commentsHidden && (
                      <button
                        className={`action-btn comment-btn ${isCommentOpen ? "active" : ""}`}
                        onClick={() => toggleComments(blog._id)}
                        type="button"
                      >
                        <svg viewBox="0 0 24 24" className="action-icon comment-icon">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        <span>{commentCounts[blog._id] ?? 0}</span>
                      </button>
                    )}

                    {/* ── SHARE — same button as View Blogs page ── */}
                    {!shareOff && (
                      <div className="share-wrapper">
                        <button
                          className="action-btn share-btn"
                          onClick={() => handleShare(blog)}
                          type="button"
                        >
                          <svg viewBox="0 0 24 24" className="action-icon share-icon">
                            <line x1="22" y1="2" x2="11" y2="13" />
                            <polygon points="22 2 15 22 11 13 2 9 22 2" />
                          </svg>
                          <span>{shareCounts[blog._id] ?? 0}</span>
                        </button>
                        {shareToast === blog._id && (
                          <span className="share-toast">Link copied!</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="actions-right">
                    {/* ── SAVE — same button as View Blogs page ── */}
                    {!saveOff && (
                      <button
                        className={`action-btn save-btn ${isSaved(blog._id) ? "saved" : ""}`}
                        onClick={() => handleSaveToggle(blog._id)}
                        type="button"
                      >
                        <svg viewBox="0 0 24 24" className="action-icon save-icon">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                        <span>{saveCounts[blog._id] ?? 0}</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* ── COMMENTS — shared Comments component with loop comments,
                     comment likes, nested replies and emoji picker ── */}
                {!commentsHidden && (
                  <div className="ap-comments-host">
                    <Comments
                      blogId={blog._id}
                      blogAuthor={author}
                      currentUsername={username}
                      currentUserPic={myProfilePic}
                      isOpen={isCommentOpen}
                    />
                  </div>
                )}

                {/* META — upload time + eye/view count (same as read blogs page) */}
                <div className="blog-meta">
                  <span className="blog-upload-time">
                    🕒 {new Date(blog.createdAt).toLocaleString()}
                  </span>
                  <span className="blog-views" title="Unique views">
                    <svg viewBox="0 0 24 24" className="view-icon">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    {blog.viewCount ?? 0}
                  </span>
                </div>
              </div>
            );
          })
        )}

        {/* Progressive loading — replaces the old per-page number buttons */}
        {!blogsError && <ListLoader list={blogList} total={blogs.length} />}
      </div>

      {/* IMAGE MODAL */}
      {modalImg && (
        <div className="ap-modal-overlay" onClick={() => setModalImg(null)}>
          <button className="ap-modal-close" onClick={() => setModalImg(null)}>✕</button>
          <div className="ap-modal-inner" onClick={e => e.stopPropagation()}>
            <img src={modalImg} className="ap-modal-img" alt="expanded" />
          </div>
        </div>
      )}

      {/* NO-STATUS SNACKBAR */}
      {statusToast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%",
          transform: "translateX(-50%)", zIndex: 9999,
          background: "rgba(20,20,30,0.95)", color: "#fff",
          padding: "10px 18px", borderRadius: 12, fontSize: 14,
          boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
        }}>
          No status available
        </div>
      )}
    </div>
  );
}

export default AuthorBlogs;